import {
  useBillingStore,
  type BillingTermKind,
  type CatalogOffering,
} from '~/store/billing'

/**
 * Money math shared by every priced surface of the checkout (term picker, plan
 * table, storage step, order summary).
 *
 * The platform meters by the hour and commits by the cycle, which makes raw
 * catalog numbers incomparable across terms: an annual "cycle total" is a year
 * of spend next to a month of it. Everything here normalises to ONE month —
 * the unit customers actually compare — so a longer commitment reads as a
 * smaller monthly number instead of a bigger invoice.
 *
 * A "month" is the MONTHLY term's own `commitment_hours` (672 h), read from the
 * catalog rather than hardcoded; every other committed term is a whole multiple
 * of it, so the normalisation is exact for commitments and an explicit 24/7
 * projection for on-demand.
 */
export function useCheckoutPricing() {
  const billing = useBillingStore()

  const monthHours = computed(
    () =>
      billing.catalog?.terms.find((t) => t.kind === 'MONTHLY')
        ?.commitment_hours ?? 672
  )

  function commitmentHours(kind: BillingTermKind): number | null {
    return (
      billing.catalog?.terms.find((t) => t.kind === kind)?.commitment_hours ??
      null
    )
  }

  /** How many months a term commits to (null for on-demand). */
  function commitmentMonths(kind: BillingTermKind): number | null {
    const hours = commitmentHours(kind)
    if (!hours || !monthHours.value) return null
    return Math.round(hours / monthHours.value)
  }

  function toBigInt(micro: string | null | undefined): bigint | null {
    if (micro == null || micro === '') return null
    try {
      return BigInt(micro)
    } catch {
      return null
    }
  }

  /** µ¢ a per-hour rate costs over one month. */
  function perMonth(rateMicroPerHour: string | null | undefined): bigint | null {
    const rate = toBigInt(rateMicroPerHour)
    if (rate == null) return null
    return rate * BigInt(monthHours.value)
  }

  /** µ¢ a per-hour rate costs over a term's whole commitment (null = on-demand). */
  function perCycle(
    rateMicroPerHour: string | null | undefined,
    kind: BillingTermKind
  ): bigint | null {
    const rate = toBigInt(rateMicroPerHour)
    const hours = commitmentHours(kind)
    if (rate == null || !hours) return null
    return rate * BigInt(hours)
  }

  /**
   * What a committed term saves against the on-demand price of the SAME thing.
   * On-demand is the only honest anchor: it is the price with no commitment,
   * so the discount is what the commitment buys. Null when there is nothing to
   * compare (on-demand itself, a missing rate, or a non-positive difference).
   */
  function savingsVsOnDemand(
    ratesByTerm: Partial<Record<BillingTermKind, string | null>> | undefined,
    kind: BillingTermKind
  ): { pct: number; perMonthMicro: bigint; perCycleMicro: bigint | null } | null {
    if (!ratesByTerm || kind === 'ON_DEMAND') return null
    const committed = toBigInt(ratesByTerm[kind])
    const onDemand = toBigInt(ratesByTerm.ON_DEMAND)
    if (committed == null || onDemand == null) return null
    if (committed <= 0n || onDemand <= 0n || committed >= onDemand) return null

    const pct = Math.round(Number(((onDemand - committed) * 100n) / onDemand))
    if (pct < 1) return null

    const deltaPerHour = onDemand - committed
    const hours = commitmentHours(kind)
    return {
      pct,
      perMonthMicro: deltaPerHour * BigInt(monthHours.value),
      perCycleMicro: hours ? deltaPerHour * BigInt(hours) : null,
    }
  }

  /** Cheapest COMPUTE plan — the "from R$ x" anchor before a plan is picked. */
  function cheapestOffering(
    offerings: CatalogOffering[],
    kind: BillingTermKind
  ): CatalogOffering | null {
    let best: CatalogOffering | null = null
    let bestRate: bigint | null = null
    for (const offering of offerings) {
      const rate = toBigInt(offering.rates_by_term[kind])
      if (rate == null || rate <= 0n) continue
      if (bestRate == null || rate < bestRate) {
        best = offering
        bestRate = rate
      }
    }
    return best
  }

  const fmt = (micro: bigint | null) =>
    micro == null ? '—' : billing.formatMicroCents(micro.toString())

  return {
    monthHours,
    commitmentHours,
    commitmentMonths,
    perMonth,
    perCycle,
    savingsVsOnDemand,
    cheapestOffering,
    /** Formats a µ¢ bigint with the store's currency formatter. */
    fmt,
  }
}
